/* eslint-disable react/prop-types */
import {
  Box,
  Card,
  Stack,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ReportGmailerrorred as ReportGmailerrorredIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import HazardAccordion from "./HazardAccordion";

function HazardsPanel({
  selectedWorkflowGroup,
  selectedWorkflowId,
  expandedHazard,
  onHazardToggle,
  onEditHazard,
  onUnlinkHazard,
  onDeleteHazard,
  onEditWorkflow,
  getRiskLevelColor,
  onUnlinkPrecaution,
  deletingPrecaution,
  onAddPrecaution,
  onAddPrecautionWithCategory,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card
      elevation={3}
      sx={{
        flex: 1,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 80,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <ReportGmailerrorredIcon
              sx={{ color: "warning.main", fontSize: 28 }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{ lineHeight: 1.2 }}
            >
              {selectedWorkflowGroup?.workflow.name ||
                t("hazards.selectWorkflow")}
            </Typography>
          </Stack>
          {selectedWorkflowId &&
            selectedWorkflowId !== "unassigned" &&
            selectedWorkflowGroup && (
              <Tooltip title={t("worktypeHazards.editTooltip")}>
                <IconButton
                  size="medium"
                  color="primary"
                  onClick={() => onEditWorkflow(selectedWorkflowGroup.workflow)}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  aria-label={t("worktypeHazards.editButtonAria", {
                    name: selectedWorkflowGroup.workflow.name,
                  })}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {selectedWorkflowGroup && selectedWorkflowGroup.hazards.length > 0 ? (
          <Box>
            {selectedWorkflowGroup.hazards.map((hazard) => (
              <HazardAccordion
                key={hazard.id}
                hazard={hazard}
                isExpanded={expandedHazard === hazard.id}
                onToggle={(expanded) =>
                  onHazardToggle(expanded ? hazard.id : null)
                }
                onEdit={() => onEditHazard(hazard)}
                onUnlink={() => onUnlinkHazard(selectedWorkflowId, hazard)}
                onDelete={() => onDeleteHazard(hazard.id)}
                showUnlinkButton={selectedWorkflowId !== "unassigned"}
                getRiskLevelColor={getRiskLevelColor}
                onUnlinkPrecaution={onUnlinkPrecaution}
                deletingPrecaution={deletingPrecaution}
                onAddPrecaution={onAddPrecaution}
                onAddPrecautionWithCategory={onAddPrecautionWithCategory}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Box
              sx={{
                display: "inline-flex",
                p: 3,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                mb: 3,
              }}
            >
              <ReportGmailerrorredIcon
                sx={{
                  fontSize: 48,
                  color: "warning.main",
                  opacity: 0.7,
                }}
              />
            </Box>
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              fontWeight="500"
            >
              {t("hazards.noHazardsInWorkflow")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("hazards.selectWorkflowToViewHazards")}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default HazardsPanel;
